// Maps unit kinds and city tiers to texture asset paths.
// Textures live in public/assets/textures/ and are PNG files.

const TEXTURE_BASE = '/assets/textures';

/** Player color label derived from faction index. */
type FactionColor = 'blue' | 'orange';

function factionColor(ownerPlayerId: unknown): FactionColor {
  const id = (ownerPlayerId as number) ?? 0;
  return id === 0 || id === 1 ? (['blue', 'orange'] as const)[id] : 'blue';
}

/** Unit kind → texture path. Falls back to warrior if kind is unknown. */
export function unitTexturePath(kind: string, ownerPlayerId?: unknown): string {
  const color = factionColor(ownerPlayerId);

  const mapped = UNIT_KIND_TO_TEXTURE[kind] ?? kind;
  return `${TEXTURE_BASE}/units/${mapped}_${color}.png`;
}

/** City tier → texture path. */
export function cityTexturePath(tier: number, ownerPlayerId?: unknown): string {
  const color = factionColor(ownerPlayerId);

  let variant: string;
  if (tier <= 1) variant = 'village_t1';
  else if (tier === 2) variant = 'city_t2';
  else variant = 'capital_t3';

  return `${TEXTURE_BASE}/cities/${variant}_${color}.png`;
}

/** Terrain biome → texture path. */
export function terrainTexturePath(biome: string): string {
  return `${TEXTURE_BASE}/terrain/tile_${biome}.png`;
}

/**
 * Maps unit kind strings (from the sim) to texture file prefixes.
 * Prefixes match filenames in public/assets/textures/units/.
 */
const UNIT_KIND_TO_TEXTURE: Record<string, string> = {
  warrior: 'warrior',
  archer: 'archer',
  cavalry: 'cavalry',
  siege: 'siege',
  healer: 'healer',
  flyer: 'flyer',
  skeleton: 'skeleton',
  necromancer: 'necromancer',
  assassin: 'skeleton', // reuse skeleton — closest silhouette
  supply_wagon: 'siege', // reuse siege — similar bulk
  explorer: 'warrior', // reuse warrior
  zealot: 'templar', // reuse templar
  crusader: 'templar',
  inquisitor: 'priest', // reuse priest
  templar: 'templar',
  blood_priest: 'shaman', // reuse shaman
  death_knight: 'necromancer', // reuse necromancer
  heretic: 'shaman',
  dark_templar: 'templar',
  luminarch: 'flyer', // flyer silhouette
  dreadlord: 'cavalry', // mounted silhouette
  wraith: 'skeleton', // skeleton silhouette
  seraphim: 'flyer',
  zombie: 'skeleton',
  slaver: 'warrior',
  golem: 'siege', // Champion Golem (legends.enabled) - bulky siege silhouette
  lich: 'necromancer', // Champion Lich (legends.enabled)
  dragon: 'flyer', // Champion (legends.enabled) — flyer silhouette until it has its own portrait
};

/** Check if a unit kind has a dedicated texture (not a fallback). */
export function hasDedicatedTexture(kind: string): boolean {
  const mapped = UNIT_KIND_TO_TEXTURE[kind];
  if (!mapped) return false;
  // Direct match = dedicated; different key = fallback
  return (
    mapped === kind ||
    [
      'warrior',
      'archer',
      'cavalry',
      'siege',
      'healer',
      'flyer',
      'skeleton',
      'necromancer',
    ].includes(mapped)
  );
}

/** Get a unique list of all texture paths we expect to load. */
export function allTexturePaths(): string[] {
  const kinds = Object.values(UNIT_KIND_TO_TEXTURE);
  const unique = [...new Set(kinds)];
  const paths: string[] = [];
  for (const k of unique) {
    paths.push(`${TEXTURE_BASE}/units/${k}_blue.png`);
    paths.push(`${TEXTURE_BASE}/units/${k}_orange.png`);
  }
  // the unit material atlas (sampled by the unit shader, `render/voxelMaterial.ts`)
  paths.push(`${TEXTURE_BASE}/units/atlas.png`);
  // city textures
  for (const variant of ['village_t1', 'city_t2', 'capital_t3']) {
    paths.push(`${TEXTURE_BASE}/cities/${variant}_blue.png`);
    paths.push(`${TEXTURE_BASE}/cities/${variant}_orange.png`);
  }
  // terrain textures
  for (const biome of [
    'plains',
    'forest',
    'hills',
    'mountain',
    'desert',
    'tundra',
    'coast',
    'ocean',
  ]) {
    paths.push(`${TEXTURE_BASE}/terrain/tile_${biome}.png`);
  }
  return paths;
}
